// ══════════════════════════════════════════════════════
//  verdict.js — 投资建议综合判断引擎
//
//  三层判断流水线：
//  Layer 1: AI 六维评分 composite + alpha (已有，从 a 对象读取)
//  Layer 2: AI 语义信号 news_signals (从 a.news_signals 读取)
//  Layer 3: 代码量化否决/加分 (本文件计算)
//
//  封顶规则：单次最多降 2 级 / 升 1 级
// ══════════════════════════════════════════════════════

const VERDICT_LEVELS = ["暂不参与", "保持观察", "建议参与"];
const VERDICT_COLOR  = { "暂不参与": "#dc2626", "保持观察": "#d97706", "建议参与": "#059669" };
const VERDICT_BG     = { "暂不参与": "#fef2f2", "保持观察": "#fffbeb", "建议参与": "#ecfdf5" };

const VERDICT_LEVELS_EN = ["Avoid", "Watch", "Consider"];
const toEn = zh => VERDICT_LEVELS_EN[VERDICT_LEVELS.indexOf(zh)] || zh;

// ── 项目阶段推断 ──────────────────────────────────────
export function inferPhase(project, a) {
  const hasToken = !!project?.details?.token_symbol || !!project?.cgData?.market_data?.current_price?.usd;
  if (!hasToken) return "no_token";

  const tgeDate = project?.tgeDate || a?.tge_date;
  let tgeMonthsAgo = null;
  if (tgeDate) tgeMonthsAgo = (Date.now() - new Date(tgeDate).getTime()) / (30 * 86400 * 1000);

  const next30dRatio = computeNext30dRatio(project);
  if (next30dRatio > 0.05) return "post_tge_high_unlock";
  if (tgeMonthsAgo != null && tgeMonthsAgo < 6) return "post_tge_early";
  return "post_tge_stable";
}

function computeNext30dRatio(project) {
  const tu = project?.tokenUnlock;
  if (!tu?.ok) return 0;
  const totalSupply = project?.cgData?.market_data?.total_supply
                    || project?.cgData?.market_data?.circulating_supply
                    || tu.totalSupply;
  if (!totalSupply || !tu.next30dAmount) return 0;
  return tu.next30dAmount / totalSupply;
}

// ── 信号提取（纯函数，无副作用） ─────────────────────
export function computeSignals(project, a) {
  const cg   = project?.cgData?.market_data || {};
  const am   = project?.artemisMetrics;
  const tu   = project?.tokenUnlock;
  const th   = project?.topHolders;
  const defi = project?.defiData;

  // 估值倍数（Q38：标 revSource 让 UI 显示「实测/估算」徽章）
  const mc  = cg.market_cap?.usd || 0;
  const fdv = cg.fully_diluted_valuation?.usd || 0;
  // 来源优先级：artemis revenue > defillama revenue > artemis fees > defillama fees
  let rev30d = 0, revSource = null;
  if (am?.ok && am.revenue30d > 0) { rev30d = am.revenue30d; revSource = 'artemis'; }
  else if (defi?.revenue?.monthly > 0) { rev30d = defi.revenue.monthly; revSource = 'defillama_rev'; }
  else if (am?.ok && am.perpFees30d > 0) { rev30d = am.perpFees30d; revSource = 'artemis_fees'; }
  else if (defi?.fees?.monthly > 0) { rev30d = defi.fees.monthly; revSource = 'defillama_fees'; }
  const fees30d = (am?.ok ? am.perpFees30d : null) || defi?.fees?.monthly || 0;
  const feesAnn = fees30d ? fees30d * (365 / 30) : 0;
  const revAnn  = rev30d  ? rev30d  * (365 / 30) : 0;
  const mcRevRatio  = mc && revAnn ? mc / revAnn : null;
  const fdvRevRatio = fdv && revAnn ? fdv / revAnn : null;

  // 解锁压力（Surf 数据由 proxy 转 delta 后是真实单次释放量，validateTokenUnlock
  // 已经做过 sanity 拦截 — verdict 层无需再防御性归零）
  const totalSupply = cg.total_supply || cg.circulating_supply || tu?.totalSupply || 0;
  const next30dPressure = (tu?.ok && tu.next30dAmount && totalSupply) ? tu.next30dAmount / totalSupply : 0;
  // 6个月解锁估算 = 30d * 6（粗略，实际应累加 events 中 days≤180 的）
  let next180dAmount = 0;
  if (tu?.ok && (tu.events || []).length) {
    next180dAmount = tu.events.filter(e => e.daysFromNow >= 0 && e.daysFromNow <= 180)
                              .reduce((s, e) => s + (e.totalAmount || 0), 0);
  }
  const next180dPressure = (next180dAmount && totalSupply) ? next180dAmount / totalSupply : 0;

  // 团队/投资方占比
  const teamAllocAmount = (tu?.allocationTotals || [])
    .filter(x => /team|invest|insider|advisor|founder|early/i.test(x.name || ""))
    .reduce((s, x) => s + (x.amount || 0), 0);
  const totalAllocAmount = (tu?.allocationTotals || []).reduce((s, x) => s + (x.amount || 0), 0);
  const teamUnlockShare = totalAllocAmount > 0 ? teamAllocAmount / totalAllocAmount : 0;

  // 持有者集中度
  const holderTop1Pct = (th?.holders?.[0]?.percentage || 0) / 100;
  const holderTop10Pct = (th?.top10Concentration || 0) / 100;

  // TVL 动能
  const tvlWeekChange = parseFloat(defi?.tvl?.weekChange || 0);

  // 价格异动（Hack/暴雷/Rug 的最及时硬信号；优先于 AI 综合判断）
  // CoinGecko market_data 字段：price_change_percentage_24h / _7d_in_currency.usd / _14d / _30d
  const priceChange24h = Number.isFinite(cg.price_change_percentage_24h)
    ? cg.price_change_percentage_24h
    : (cg.price_change_percentage_24h_in_currency?.usd ?? 0);
  const priceChange7d  = cg.price_change_percentage_7d_in_currency?.usd
                      ?? cg.price_change_percentage_7d
                      ?? 0;
  const priceChange30d = cg.price_change_percentage_30d_in_currency?.usd
                      ?? cg.price_change_percentage_30d
                      ?? 0;

  // AI 新闻语义信号
  // EDGE Markets 教训：未发币项目若 Surf 误命中其他 token 项目，会拉回"币价闪崩 71%"
  // 这类红旗作为污染信号写入。下面在 inferPhase=no_token 时做防御性过滤：
  // 凡含价格/代币/解锁/上线/Hack 关键词的 flag 一律剔除，因为没发币就不可能有这些事件。
  const TOKEN_RELATED_RE = /代币|币价|币种|价格|暴跌|闪崩|腰斩|hack|exploit|unlock|解锁|上线|listing|listed|TGE|空投|airdrop|burn|销毁|stake|质押|\$[A-Z]{2,}/i;

  const rawNewsRed   = (a?.news_signals?.red_flags   || []).filter(Boolean);
  const rawNewsGreen = (a?.news_signals?.green_flags || []).filter(Boolean);
  const newsSentiment = a?.news_signals?.sentiment;
  const _isNoToken = !(project?.details?.token_symbol || project?.cgData?.market_data?.current_price?.usd);
  const newsRed   = _isNoToken ? rawNewsRed.filter(x   => !TOKEN_RELATED_RE.test(x)) : rawNewsRed;
  const newsGreen = _isNoToken ? rawNewsGreen.filter(x => !TOKEN_RELATED_RE.test(x)) : rawNewsGreen;

  // firstDoc 与首次融资的时间差（doc 早于融资 → 正值；doc 晚于融资 → 负值）
  // 正值大 = 团队有规划，做事先于资本；负值大 = 融资先行，产品/文档滞后
  let firstDocLeadDays = null;
  try {
    const fdEntry = (project?.timeline || []).find(e => e.key === 'firstDoc');
    const firstDocDate = fdEntry?.date;
    const fundingDates = (project?.fundingRounds || [])
      .map(r => {
        if (r?.published_time) return r.published_time;
        if (typeof r?.date === 'number') return new Date(r.date * 1000).toISOString().slice(0, 10);
        if (typeof r?.date === 'string') return r.date.slice(0, 10);
        if (r?.announced_date) return r.announced_date;
        return null;
      })
      .filter(Boolean)
      .sort();
    const firstFundingDate = fundingDates[0];
    if (firstDocDate && firstFundingDate) {
      const dDoc = new Date(firstDocDate).getTime();
      const dFund = new Date(firstFundingDate).getTime();
      if (Number.isFinite(dDoc) && Number.isFinite(dFund)) {
        firstDocLeadDays = Math.round((dFund - dDoc) / 86400000);
      }
    }
  } catch {}

  // 解锁数据可信度：proxy `/api/token-unlock` 短符号 + 0 数据时 ok=false（reason=short_symbol_unconfirmed）
  // 没有真实 events 时不能假设 "无压力"
  const hasUnlockData = !!(tu && tu.ok === true && Array.isArray(tu.events) && tu.events.length > 0);

  // projectContext 信号（来自 synthesizeProjectContext，30d 缓存于 Supabase project_context）
  // 设计：只把结构化字段提进 signals，不让 AI 自由文本进入硬规则
  const pc = project?.projectContext || null;
  const pcStage         = pc?.productMaturity?.stage || null;           // idea | devnet | testnet | mainnet | scaling | mature
  const pcTechDepth     = (typeof pc?.technicalDepth?.score === 'number') ? pc.technicalDepth.score : null; // 0-10
  const pcEvents30d     = (typeof pc?.recentMomentum?.productEvents30d === 'number') ? pc.recentMomentum.productEvents30d : null;
  const pcLiveFeatures  = Array.isArray(pc?.productFeatures) ? pc.productFeatures.filter(f => f?.maturity === 'live').length : 0;
  const pcRisksCount    = Array.isArray(pc?.knownRisks) ? pc.knownRisks.length : 0;
  const hasProjectContext = !!pc;

  // 团队信号（来自 enhancedTeam 合成层 + 客观字段）
  // 设计：只用客观硬数据（实名度 / 团队规模 / 头部前作数），qualityScore 不作判据
  // 避免合成层 AI 误差传染到 verdict（qualityScore 用于评分/UI 展示，不进 verdict 规则）
  const et = project?.enhancedTeam || null;
  const teamFoundersCount   = Array.isArray(et?.founders) ? et.founders.length : 0;
  const teamHasDoxxed       = !!(et?.summary?.hasDoxxedFounders);
  const teamSize            = et?.summary?.teamSize ?? teamFoundersCount;
  const teamProminentCount  = Array.isArray(et?.summary?.prominentBackgrounds) ? et.summary.prominentBackgrounds.length : 0;
  // hasTeamData = 至少一个数据源识别到 founder（避免在没扫到的情况下错误触发"匿名"红旗）
  const hasTeamData         = !!(et && (et.founders?.length > 0 || et.topContributors?.length > 0));

  // 合规风险信号（来自 synthesizeComplianceRisk A2 服务；只用结构化字段）
  const cr = project?.complianceRisk || null;
  const crCritical          = cr && Array.isArray(cr.findings) ? cr.findings.filter(f => f.severity === 'critical').length : 0;
  const crHigh              = cr && Array.isArray(cr.findings) ? cr.findings.filter(f => f.severity === 'high').length : 0;
  const crTopicsConsidered  = cr && Array.isArray(cr.topics_considered) ? cr.topics_considered.length : 0;
  const hasComplianceRisk   = !!cr && !cr._error && Array.isArray(cr.findings);

  return {
    mc, fdv, feesAnn, revAnn,
    mcRevRatio, fdvRevRatio, revSource, // Q38: 来源标注（让 UI 标 实测/估算 徽章）
    next30dPressure, next180dPressure, hasUnlockData,
    teamUnlockShare,
    holderTop1Pct, holderTop10Pct,
    tvlWeekChange,
    priceChange24h, priceChange7d, priceChange30d,
    newsRed, newsGreen, newsSentiment,
    firstDocLeadDays,
    teamFoundersCount, teamHasDoxxed, teamSize, teamProminentCount, hasTeamData,
    pcStage, pcTechDepth, pcEvents30d, pcLiveFeatures, pcRisksCount, hasProjectContext,
    crCritical, crHigh, crTopicsConsidered, hasComplianceRisk,
    composite: a?.composite || 0,
  };
}

// ── 红绿旗规则集 ────────────────────────────────────
// category 用于 no_token 项目跳过：unlock / holder / valuation / tvl / news
const RED_FLAGS = [
  { category: 'unlock',    test: s => s.next30dPressure > 0.05,                          weight: 2, reasonZhFn: s => `30日解锁 ${(s.next30dPressure*100).toFixed(1)}% 流通量（高抛压）`, reasonEn: s => `30d unlock ${(s.next30dPressure*100).toFixed(1)}% of supply (high pressure)` },
  { category: 'unlock',    test: s => s.next180dPressure > 0.30,                         weight: 1, reasonZhFn: s => `6个月解锁 ${(s.next180dPressure*100).toFixed(0)}% 流通量`, reasonEn: s => `180d unlock ${(s.next180dPressure*100).toFixed(0)}% of supply` },
  { category: 'unlock',    test: s => s.teamUnlockShare > 0.40,                          weight: 1, reasonZhFn: s => `团队/投资方占比 ${(s.teamUnlockShare*100).toFixed(0)}%`, reasonEn: s => `Team/investor allocation ${(s.teamUnlockShare*100).toFixed(0)}%` },
  { category: 'holder',    test: s => s.holderTop1Pct > 0.30,                            weight: 2, reasonZhFn: s => `最大持有者 ${(s.holderTop1Pct*100).toFixed(1)}% 中心化风险`, reasonEn: s => `Top holder ${(s.holderTop1Pct*100).toFixed(1)}% (centralization risk)` },
  { category: 'valuation', test: s => s.mcRevRatio != null && s.mcRevRatio > 80 && s.revAnn > 0, weight: 1, reasonZhFn: s => `MC/Rev ${s.mcRevRatio.toFixed(1)}x 估值过热`, reasonEn: s => `MC/Rev ${s.mcRevRatio.toFixed(1)}x (overvalued)` },
  { category: 'tvl',       test: s => s.tvlWeekChange < -20,                             weight: 1, reasonZhFn: s => `TVL 周度 ${s.tvlWeekChange.toFixed(0)}% 资金外流`, reasonEn: s => `TVL weekly ${s.tvlWeekChange.toFixed(0)}% (capital outflow)` },
  { category: 'news',      test: s => s.newsRed.length > 0,                              weight: 2, reasonZhFn: s => `近期负面新闻：${s.newsRed.slice(0, 2).join("、")}`, reasonEn: s => `Recent negative news: ${s.newsRed.slice(0, 2).join(", ")}` },
  // 团队：全匿名 + 单人项目（保守阈值，避免合成层评分误差传染。规则只用客观字段）
  { category: 'team',      test: s => s.hasTeamData && !s.teamHasDoxxed && s.teamFoundersCount <= 1, weight: 1, reasonZhFn: () => `团队全匿名 + 单人项目（实名度低 + 规模不足）`, reasonEn: () => `Anonymous + solo team (low doxxed + undersized)` },
  // 价格暴跌（Hack/暴雷/Rug 即时硬信号；24h > -50% 必触发 RED；> -70% 后续会被 hardOverride 强制 "暂不参与"）
  { category: 'price',     test: s => s.priceChange24h < -50,                             weight: 3, reasonZhFn: s => `24小时暴跌 ${s.priceChange24h.toFixed(0)}%（疑似 Hack/暴雷）`, reasonEn: s => `24h crash ${s.priceChange24h.toFixed(0)}% (possible exploit/incident)` },
  { category: 'price',     test: s => s.priceChange7d  < -60 && s.priceChange24h >= -50, weight: 2, reasonZhFn: s => `7日累计跌 ${s.priceChange7d.toFixed(0)}%（趋势恶化）`, reasonEn: s => `7d down ${s.priceChange7d.toFixed(0)}% (deteriorating trend)` },
  // firstDoc 晚于首次融资 1 年以上：融资先行、产品滞后（警示）
  { category: 'firstDoc',  test: s => s.firstDocLeadDays != null && s.firstDocLeadDays < -365, weight: 1, reasonZhFn: s => `首次公开文档比首轮融资晚 ${Math.abs(s.firstDocLeadDays/30).toFixed(0)} 个月（产品/叙事滞后）`, reasonEn: s => `First docs published ${Math.abs(Math.round(s.firstDocLeadDays/30))}mo AFTER first funding (product/narrative lag)` },
  // projectContext：产品仍在 idea/devnet 阶段 — 真实产品尚未跑通
  { category: 'projectContext', test: s => s.hasProjectContext && (s.pcStage === 'idea' || s.pcStage === 'devnet'), weight: 2, reasonZhFn: s => `产品仍处 ${s.pcStage} 阶段（无可验证主网产品）`, reasonEn: s => `Product still at ${s.pcStage} stage (no live product to verify)` },
  // projectContext：技术深度极低（<3/10），代码原创度/技术壁垒不足
  { category: 'projectContext', test: s => s.hasProjectContext && s.pcTechDepth != null && s.pcTechDepth < 3, weight: 1, reasonZhFn: s => `技术深度 ${s.pcTechDepth}/10（代码原创度/技术壁垒低）`, reasonEn: s => `Technical depth ${s.pcTechDepth}/10 (low originality / weak moat)` },
  // projectContext：合成层识别出 ≥3 个具体风险
  { category: 'projectContext', test: s => s.hasProjectContext && s.pcRisksCount >= 3, weight: 1, reasonZhFn: s => `项目画像识别 ${s.pcRisksCount} 个具体风险点`, reasonEn: s => `Project context flagged ${s.pcRisksCount} specific risks` },
  // 合规规则全部解耦（2026-06-22）—— 合规卡重新定位为「机构合规尽调清单」
  // （institutional diligence），不是项目降级理由。
  // 一个项目本身可以 100% legit，但在 HK 没拿 VATP / 漏 Travel Rule 等只代表
  // 「香港机构想投这个项目要解决什么合规问题」，不应该让项目 verdict 因此降级。
  // 旧规则迁移记录：
  //   - crCritical >= 1, weight 2 红旗（已删）
  //   - crHigh >= 3,     weight 1 红旗（已删）
  // 对称地下方 GREEN_FLAGS 里的 crTopicsConsidered 绿旗也会被删除。
];

const GREEN_FLAGS = [
  { category: 'valuation', test: s => s.mcRevRatio != null && s.mcRevRatio < 15 && s.revAnn > 50_000_000, weight: 2, reasonZhFn: s => `MC/Rev ${s.mcRevRatio.toFixed(1)}x，年化收入 $${(s.revAnn/1e6).toFixed(0)}M（估值合理）`, reasonEn: s => `MC/Rev ${s.mcRevRatio.toFixed(1)}x with $${(s.revAnn/1e6).toFixed(0)}M annual revenue (fair valuation)` },
  // 解锁稳定 GREEN：必须有真实 events 数据才触发，避免 "缺数据" 被当成 "无压力" 误报
  // （短符号如 "H" 返回 0 events 时，proxy 已返回 ok:false → hasUnlockData=false）
  { category: 'unlock',    test: s => s.hasUnlockData && s.next30dPressure < 0.005 && s.next180dPressure < 0.05, weight: 1, reasonZhFn: () => `解锁稳定期，近6个月无显著抛压`, reasonEn: () => `Unlock stable period (no major pressure 180d)` },
  { category: 'tvl',       test: s => s.tvlWeekChange > 20,                                               weight: 1, reasonZhFn: s => `TVL 周增 +${s.tvlWeekChange.toFixed(0)}% 资金流入`, reasonEn: s => `TVL weekly +${s.tvlWeekChange.toFixed(0)}% (capital inflow)` },
  { category: 'news',      test: s => s.newsGreen.length > 0,                                             weight: 1, reasonZhFn: s => `近期正面动态：${s.newsGreen.slice(0, 2).join("、")}`, reasonEn: s => `Recent positive news: ${s.newsGreen.slice(0, 2).join(", ")}` },
  // 团队：实名 + 规模充足（保守阈值）
  { category: 'team',      test: s => s.hasTeamData && s.teamHasDoxxed && s.teamSize >= 5,                weight: 1, reasonZhFn: s => `团队实名 + ${s.teamSize}+ 核心成员可见`, reasonEn: s => `Doxxed team + ${s.teamSize}+ visible core members` },
  // 团队：含 2 个以上头部前作（Ex-Coinbase / Ex-Polygon 等）
  { category: 'team',      test: s => s.hasTeamData && s.teamProminentCount >= 2,                         weight: 1, reasonZhFn: s => `团队含 ${s.teamProminentCount} 个头部前作背景`, reasonEn: s => `Team has ${s.teamProminentCount} prominent prior backgrounds` },
  // firstDoc 早于首次融资 6 个月以上：团队规划清晰，做事先于资本（正向）
  { category: 'firstDoc',  test: s => s.firstDocLeadDays != null && s.firstDocLeadDays > 180, weight: 1, reasonZhFn: s => `文档比融资早 ${Math.round(s.firstDocLeadDays/30)} 个月（团队早有规划）`, reasonEn: s => `Docs published ${Math.round(s.firstDocLeadDays/30)}mo before first funding (team prepared early)` },
  // projectContext：主网已上线 + 技术深度高（≥7/10）
  { category: 'projectContext', test: s => s.hasProjectContext && ['mainnet','scaling','mature'].includes(s.pcStage) && s.pcTechDepth != null && s.pcTechDepth >= 7, weight: 2, reasonZhFn: s => `${s.pcStage} 阶段 + 技术深度 ${s.pcTechDepth}/10（高壁垒）`, reasonEn: s => `${s.pcStage} stage + tech depth ${s.pcTechDepth}/10 (strong moat)` },
  // projectContext：近 30 天活跃产品迭代（≥3 个产品类事件）
  { category: 'projectContext', test: s => s.hasProjectContext && s.pcEvents30d != null && s.pcEvents30d >= 3, weight: 1, reasonZhFn: s => `近 30 天 ${s.pcEvents30d} 个产品迭代事件`, reasonEn: s => `${s.pcEvents30d} product iterations in last 30d` },
  // projectContext：≥3 个 live 产品功能，证明产品矩阵完整
  { category: 'projectContext', test: s => s.hasProjectContext && s.pcLiveFeatures >= 3, weight: 1, reasonZhFn: s => `${s.pcLiveFeatures} 个 live 产品功能（矩阵完整）`, reasonEn: s => `${s.pcLiveFeatures} live product features (complete matrix)` },
  // 合规绿旗（crTopicsConsidered + 0 critical/high）2026-06-22 解耦 — 见 RED_FLAGS 末尾注释
];

const SKIP_CATEGORIES_NO_TOKEN = new Set(['unlock', 'holder', 'valuation', 'tvl']);

// ── 基础 verdict（沿用原公式）─────────────────────
function computeBaseVerdict(composite, alpha) {
  if (alpha > 150 && composite >= 78) return "建议参与";
  if (alpha > 70  && composite >= 62) return "保持观察";
  return "暂不参与";
}

// ── 净分 → 档位调整（封顶 -2/+1）─────────────────
function applyOverlay(baseVerdict, redScore, greenScore) {
  let netScore = greenScore - redScore;
  // 封顶：升级最多 +1（一档），降级最多 -2（两档）
  if (netScore > 1) netScore = 1;
  if (netScore < -2) netScore = -2;

  const baseIdx = VERDICT_LEVELS.indexOf(baseVerdict);
  let newIdx = baseIdx + netScore;
  if (newIdx < 0) newIdx = 0;
  if (newIdx >= VERDICT_LEVELS.length) newIdx = VERDICT_LEVELS.length - 1;
  return VERDICT_LEVELS[newIdx];
}

// ── 操作要点（混合：规则模板 + AI fdv_tiers）──────
function buildActionGuide(project, a, signals, finalVerdict, lang) {
  const guides = [];
  const en = lang === 'en';

  // 仓位上限（基于解锁压力 + 中心化风险）
  let positionCap = 5;
  if (signals.next30dPressure > 0.05) positionCap = 2;
  else if (signals.next30dPressure > 0.02) positionCap = 3;
  if (signals.holderTop1Pct > 0.30) positionCap = Math.min(positionCap, 2);
  if (finalVerdict === "暂不参与") positionCap = 0;
  else if (finalVerdict === "保持观察") positionCap = Math.min(positionCap, 2);

  if (positionCap > 0) {
    guides.push(en ? `Position cap: ${positionCap}% of portfolio` : `仓位上限：${positionCap}%（受信号约束）`);
  }

  // 等待时点（基于解锁事件）
  const upcomingHigh = (project?.tokenUnlock?.events || [])
    .filter(e => e.daysFromNow >= 0 && e.daysFromNow <= 60)
    .sort((a, b) => b.totalAmount - a.totalAmount)[0];
  if (upcomingHigh && signals.next30dPressure > 0.02) {
    guides.push(en
      ? `Wait until ${upcomingHigh.date} unlock event before considering entry`
      : `建议等待 ${upcomingHigh.date} 解锁事件后再考虑入场`);
  }

  // 入场 FDV 区间（用 AI 给的 fdv_tiers S 档）
  // 字段名修正：prompt schema 是 fdv_range（line 718 in ai.js），不是 range。
  // 老代码读 sTier.range 永远 undefined，UI 字面渲染 "undefined（S 档）"（Hyperliquid 2026-06-24）。
  // 加 null 兜底：AI 未给 fdv_range 就跳过整条 guide，不输出含 "undefined" 的字符串。
  const sTier = (a?.fdv_tiers || []).find(t => t.rating === "S");
  const sTierRange = sTier?.fdv_range || sTier?.range; // 兼容老缓存
  if (sTier && sTierRange && finalVerdict !== "暂不参与") {
    guides.push(en
      ? `Optimal entry FDV: ${sTierRange} (S-tier)`
      : `最优入场 FDV：${sTierRange}（S 档）`);
  }

  // 估值 anchoring（Q38: 加 revSource 徽章让用户分辨实测 vs 估算）
  if (signals.mcRevRatio && signals.mcRevRatio < 20) {
    const srcBadge = signals.revSource
      ? (en
          ? ({ artemis: ' (📊 Artemis revenue)', defillama_rev: ' (📊 DefiLlama revenue)', artemis_fees: ' (📊 Artemis fees only)', defillama_fees: ' (📊 DefiLlama fees only)' }[signals.revSource] || '')
          : ({ artemis: '（📊 Artemis 实测）', defillama_rev: '（📊 DefiLlama 实测）', artemis_fees: '（📊 Artemis 仅手续费）', defillama_fees: '（📊 DefiLlama 仅手续费）' }[signals.revSource] || ''))
      : (en ? ' (🤖 AI estimate)' : '（🤖 AI 估算，无实测）');
    guides.push(en
      ? `Current MC/Rev ${signals.mcRevRatio.toFixed(1)}x${srcBadge} supports current valuation`
      : `当前 MC/Rev ${signals.mcRevRatio.toFixed(1)}x${srcBadge}，估值有收入支撑`);
  }

  return guides;
}

// ── 主入口 ───────────────────────────────────────
export function computeVerdict(project, a, alpha, lang = 'zh') {
  if (!a) return null;

  const signals = computeSignals(project, a);
  const phase = inferPhase(project, a);

  // 未发币项目跳过 unlock/holder 否决，保留新闻 + 估值 signals 中 token 相关字段为 0 自然失效
  const isNoToken = phase === "no_token";

  const triggeredRed = [];
  const triggeredGreen = [];
  let redScore = 0, greenScore = 0;

  const safeCall = (fn, sig, fallback) => {
    try { return fn(sig); } catch { return fallback; }
  };

  for (const f of RED_FLAGS) {
    // 未发币时跳过解锁/持有者/估值/TVL 类规则（新闻规则保留）
    if (isNoToken && SKIP_CATEGORIES_NO_TOKEN.has(f.category)) continue;
    let triggered = false;
    try { triggered = !!f.test(signals); } catch { triggered = false; }
    if (triggered) {
      const zh = safeCall(f.reasonZhFn, signals, '');
      const en = safeCall(f.reasonEn,   signals, '');
      if (zh || en) {
        triggeredRed.push({ zh, en, weight: f.weight, category: f.category });
        redScore += f.weight;
      }
    }
  }
  for (const f of GREEN_FLAGS) {
    if (isNoToken && SKIP_CATEGORIES_NO_TOKEN.has(f.category)) continue;
    let triggered = false;
    try { triggered = !!f.test(signals); } catch { triggered = false; }
    if (triggered) {
      const zh = safeCall(f.reasonZhFn, signals, '');
      const en = safeCall(f.reasonEn,   signals, '');
      if (zh || en) {
        triggeredGreen.push({ zh, en, weight: f.weight, category: f.category });
        greenScore += f.weight;
      }
    }
  }

  const baseVerdict  = computeBaseVerdict(signals.composite, alpha);
  let finalVerdict   = applyOverlay(baseVerdict, redScore, greenScore);

  // ── 硬性覆盖（hardOverride）─────────────────────────
  // 极端价格暴跌（24h <-70% 或 7d <-80%）绕过 -2/+1 封顶规则，强制 "暂不参与"。
  // 用于 Hack / Exploit / Rugpull 等灾难事件——AI 综合判断在这种 case 下不能再
  // "保持观察"。这是最后一道防线，即使 Surf 漏新闻、AI 漏识别也能兜底。
  let hardOverride = null;
  if (signals.priceChange24h < -70) {
    hardOverride = `24h_crash_${signals.priceChange24h.toFixed(0)}pct`;
    finalVerdict = "暂不参与";
  } else if (signals.priceChange7d < -80) {
    hardOverride = `7d_crash_${signals.priceChange7d.toFixed(0)}pct`;
    finalVerdict = "暂不参与";
  }

  const downgraded   = VERDICT_LEVELS.indexOf(finalVerdict) < VERDICT_LEVELS.indexOf(baseVerdict);
  const upgraded     = VERDICT_LEVELS.indexOf(finalVerdict) > VERDICT_LEVELS.indexOf(baseVerdict);

  const actionGuide = buildActionGuide(project, a, signals, finalVerdict, lang);

  return {
    finalVerdict,
    finalVerdictEn: toEn(finalVerdict),
    baseVerdict,
    baseVerdictEn: toEn(baseVerdict),
    color: VERDICT_COLOR[finalVerdict],
    bg: VERDICT_BG[finalVerdict],
    triggeredRed,
    triggeredGreen,
    redScore,
    greenScore,
    netScore: greenScore - redScore,
    downgraded,
    upgraded,
    phase,
    signals,
    actionGuide,
    // 两种状态分开：
    //   hasNewsSignals   = 真有 red/green/sentiment 信号触发（用于颜色徽章 / 投票）
    //   newsSignalsState = 数据流状态: 'absent'（未读到，AI 没返回字段）|
    //                                 'empty'（读到了但无重要信号）| 'active'（有信号）
    hasNewsSignals: !!(a?.news_signals && (signals.newsRed.length || signals.newsGreen.length || signals.newsSentiment != null)),
    newsSignalsState: !a?.news_signals
      ? 'absent'
      : (signals.newsRed.length || signals.newsGreen.length || signals.newsSentiment != null)
        ? 'active'
        : 'empty',
    hardOverride,
  };
}
